// The message menu's extra items (PARITY P313 «Copiar tabla», P317 «Abrir el hilo en una ventana»,
// P346 «Ver respuestas»), in the Mac's places (MacMessageRow.messageMenu): the table's item leads
// when the right-click landed on a table (like a picture's), the window item follows «Responder en
// hilo», the answers follow «Reenviar».
import { useContext } from 'react';
import { MessageAttachments } from '../../../core/messageAttachments';
import type { PostRecord } from '../../../core/records';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { ShellContext } from '../../shell/ShellContext';
import { openAcknowledgements } from './AcknowledgementSheet';
import { copyTable, type TableRef } from './tableCopy';
import { isThreadWindow, openThreadWindow } from './threadWindow';

function useToast(): (text: string, tone?: 'info' | 'error') => void {
  const shell = useContext(ShellContext);
  return (text, tone) => shell?.toast(text, tone);
}

export function TableMenuItems({ table, onDone }: { table: TableRef | null; onDone(): void }) {
  const toast = useToast();
  if (!table) return null;
  return (
    <>
      <button
        className="menu-item"
        role="menuitem"
        data-menu="copy-table"
        onClick={() => {
          onDone();
          void copyTable(table).then((ok) => toast(ok ? t('web.extras.tableCopied') : t('web.extras.copyFailed'), ok ? 'info' : 'error'));
        }}
      >
        <Icon name="copy" size={16} /> {t('channel.action.copyTable')}
      </button>
      <div className="menu-sep" />
    </>
  );
}

/** The link a right-click landed on, or null: only real web/mail/phone links, never an in-app button. */
export function linkUnder(target: EventTarget | null): string | null {
  const a = target instanceof Element ? target.closest('a[href]') : null;
  const href = a?.getAttribute('href') ?? '';
  return /^(https?:|mailto:|tel:)/i.test(href) ? href : null;
}

/** «Copiar enlace» of the LINK under the right-click — first, like a picture's or a table's items;
 *  the message's own permalink keeps «Copiar enlace del mensaje» below (user, 2026-10-28). */
export function LinkMenuItems({ url, onDone }: { url: string | null; onDone(): void }) {
  const toast = useToast();
  if (!url) return null;
  return (
    <>
      <button
        className="menu-item"
        role="menuitem"
        data-menu="copy-url"
        onClick={() => {
          onDone();
          void navigator.clipboard?.writeText(url).then(
            () => toast(t('web.msg.linkCopied')),
            () => toast(t('web.extras.copyFailed'), 'error'),
          );
        }}
      >
        <Icon name="link" size={16} /> {t('channel.action.copyUrl')}
      </button>
      <div className="menu-sep" />
    </>
  );
}

export function ThreadWindowMenuItem({ post, onDone }: { post: PostRecord; onDone(): void }) {
  const toast = useToast();
  if (isThreadWindow()) return null;
  return (
    <button
      className="menu-item"
      role="menuitem"
      data-menu="thread-window"
      onClick={() => {
        onDone();
        if (!openThreadWindow(post.rootId || post.id)) toast(t('web.extras.threadWindow.blocked'), 'error');
      }}
    >
      <Icon name="external" size={16} /> {t('mac.openInWindow')}
    </button>
  );
}

export function AcknowledgementMenuItem({ post, onDone }: { post: PostRecord; onDone(): void }) {
  if (!MessageAttachments.isAcknowledgement(post.props)) return null;
  return (
    <button
      className="menu-item"
      role="menuitem"
      data-menu="acknowledgements"
      onClick={() => {
        onDone();
        openAcknowledgements(post.id);
      }}
    >
      <Icon name="clockCheck" size={16} /> {t('ack.open')}
    </button>
  );
}
